import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Single-select dropdown. Click-outside to close, keyboard accessible (basic).
 * Replaces the legacy <Dropdown> component from NewOrderPage with a Tailwind+lucide version.
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = 'Wybierz...',
  className,
  disabled = false,
  size = 'md',
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const sizeClasses = {
    sm: 'h-8  text-xs    px-2.5 pr-8',
    md: 'h-10 text-sm    px-3.5 pr-9',
    lg: 'h-11 text-[15px] px-4   pr-10',
  }[size];

  return (
    <div ref={ref} className={cn('relative w-full', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={cn(
          'w-full flex items-center text-left bg-white dark:bg-ink-900 border rounded-md font-medium tracking-tightish',
          'transition-colors duration-150 outline-none',
          'focus-visible:ring-4 focus-visible:ring-brand-500/10',
          'disabled:bg-ink-50 disabled:text-ink-400 disabled:cursor-not-allowed dark:disabled:bg-ink-800',
          open ? 'border-brand-500 ring-4 ring-brand-500/10' : 'border-ink-200 hover:border-ink-300 dark:border-ink-700 dark:hover:border-ink-600',
          value ? 'text-ink-900 dark:text-ink-100' : 'text-ink-400 dark:text-ink-500',
          sizeClasses,
        )}
      >
        <span className="flex-1 truncate">{value || placeholder}</span>
        <ChevronDown
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 dark:text-ink-500 transition-transform duration-200',
            open && 'rotate-180 text-brand-600 dark:text-brand-400'
          )}
        />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-white dark:bg-ink-900 border border-ink-200 dark:border-ink-700 rounded-md shadow-lg overflow-hidden animate-scale-in origin-top">
          <div className="max-h-60 overflow-y-auto py-1">
            {options.map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => { onChange(opt); setOpen(false); }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                  value === opt
                    ? 'bg-brand-50 text-brand-700 font-semibold dark:bg-brand-900/40 dark:text-brand-300'
                    : 'text-ink-800 hover:bg-ink-50 dark:text-ink-200 dark:hover:bg-ink-800'
                )}
              >
                <Check className={cn('w-4 h-4 flex-shrink-0', value === opt ? 'text-brand-600 dark:text-brand-400' : 'invisible')} />
                <span className="truncate">{opt}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select dropdown with chips inside the trigger.
 */
export function MultiSelect({
  values = [],
  onChange,
  options,
  placeholder = 'Wybierz...',
  className,
  disabled = false,
  maxChipsVisible = 8,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (opt) => {
    if (values.includes(opt)) onChange(values.filter(v => v !== opt));
    else onChange([...values, opt]);
  };

  return (
    <div ref={ref} className={cn('relative w-full', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={cn(
          'w-full min-h-[40px] flex items-center flex-wrap gap-1.5 text-left bg-white dark:bg-ink-900 border rounded-md',
          'py-1.5 pl-2 pr-9 transition-colors duration-150 outline-none',
          'focus-visible:ring-4 focus-visible:ring-brand-500/10',
          'disabled:bg-ink-50 disabled:cursor-not-allowed dark:disabled:bg-ink-800',
          open ? 'border-brand-500 ring-4 ring-brand-500/10' : 'border-ink-200 hover:border-ink-300 dark:border-ink-700 dark:hover:border-ink-600',
        )}
      >
        {values.length === 0 ? (
          <span className="text-sm text-ink-400 dark:text-ink-500 px-1.5">{placeholder}</span>
        ) : (
          <>
            {values.slice(0, maxChipsVisible).map(v => (
              <span
                key={v}
                onClick={(e) => { e.stopPropagation(); toggle(v); }}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 rounded text-xs font-semibold ring-1 ring-inset ring-brand-200/60 dark:ring-brand-700/60 hover:bg-brand-100 dark:hover:bg-brand-900/60 cursor-pointer"
              >
                {v}
                <span className="text-brand-500/70 hover:text-danger text-sm leading-none">×</span>
              </span>
            ))}
            {values.length > maxChipsVisible && (
              <span className="text-xs text-ink-500 dark:text-ink-400 px-1">+{values.length - maxChipsVisible}</span>
            )}
          </>
        )}
        <ChevronDown
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 dark:text-ink-500 transition-transform duration-200',
            open && 'rotate-180 text-brand-600 dark:text-brand-400'
          )}
        />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-white dark:bg-ink-900 border border-ink-200 dark:border-ink-700 rounded-md shadow-lg overflow-hidden animate-scale-in origin-top">
          <div className="max-h-60 overflow-y-auto py-1">
            {options.map(opt => {
              const selected = values.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggle(opt)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                    selected
                      ? 'bg-brand-50 text-brand-700 font-semibold dark:bg-brand-900/40 dark:text-brand-300'
                      : 'text-ink-800 hover:bg-ink-50 dark:text-ink-200 dark:hover:bg-ink-800'
                  )}
                >
                  <span className={cn(
                    'flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
                    selected ? 'bg-brand-600 border-brand-600' : 'border-ink-300 dark:border-ink-600'
                  )}>
                    {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </span>
                  <span className="truncate">{opt}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Select;
